import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React from 'react';
import { normalRuleRegex, specialRuleRegex, validateJPPhoneNumber } from './PhoneNumberValidation';

export function enforceFormat(value: string): string {
    let enforcedValue = value || ' ';

    enforcedValue = enforcedValue.trimStart();

    enforcedValue = removeBracketsIfValueStartsWithBracket(enforcedValue);

    enforcedValue = removeNonAllowedCharactersInTextbox(enforcedValue);

    enforcedValue = removeSpacesAfterPlus(enforcedValue);

    enforcedValue = replace00withPlus(enforcedValue);

    enforcedValue = removeMultiplePluses(enforcedValue);

    enforcedValue = formatInternationalAsString(enforcedValue);

    return enforcedValue;
}

// remove spaces after '+'
export function removeSpacesAfterPlus(value: string): string {
    return value.replace(/^\+(\s)*/, '+');
}
// remove multiple '+'
export function removeMultiplePluses(value: string): string {
    if (value.startsWith('+')) {
        value = value.substring(1, value.length);
        return `+${value.replace(/\+/g, '')}`;
    }
    return value;
}

export function removeBracketsIfValueStartsWithBracket(value: string): string {
    if (value.startsWith('(')) {
        value = value.replace(/[\(\)]/g, '');
    }
    return value;
}

export function replace00withPlus(value: string): string {
    if (value.startsWith('00') && value.length > 3) {
        // replace numbers starting with 00 with '+' once another character is entered
        value = value.replace(/^00/, '+');
    }
    return value;
}

export function removeNonAllowedCharactersInTextbox(value: string): string {
    return value.replace(/[^0-9\-\+\(\)\.#\s]/g, '');
}

export function formatInternationalAsString(value: string): string {
    const phoneNumber = parsePhoneNumberFromString(value);
    return phoneNumber ? phoneNumber.formatInternational() : value;
}

export function collapseDuplicateJPMobileTrunkPrefix(
    value: string,
    currentCountry?: string,
    currentCallingCode?: string,
): string {
    if (currentCountry !== 'JP') {
        return value;
    }

    const callingCode =
        currentCallingCode && currentCallingCode !== 'undefined' ? currentCallingCode : '';
    const valueAfterCallingCode =
        callingCode && value.startsWith(callingCode) ? value.slice(callingCode.length) : value;
    const digitsAfterCallingCode = valueAfterCallingCode.replace(/[^0-9]/g, '');

    if (!/^00(60|70|80|90)\d{7,8}$/.test(digitsAfterCallingCode)) {
        return value;
    }

    const normalizedValueAfterCallingCode = valueAfterCallingCode.replace(/^([^0-9]*)00/, '$10');

    return callingCode && value.startsWith(callingCode)
        ? `${callingCode}${normalizedValueAfterCallingCode}`
        : normalizedValueAfterCallingCode;
}

/**
 * phone number convert to JP format
 * if converted phone number is accord with JID phone format return converted phone number,
 * otherwise return removed non allowed characters phone number
 * @param value
 */
export function formatNationalAsString(value: string): string {
    const newValue = removeNonAllowedCharactersInTextbox(value);

    if (
        (validateJPPhoneNumber(newValue) && normalRuleRegex.test(newValue)) ||
        specialRuleRegex.test(newValue)
    ) {
        return newValue;
    }

    const stripped = newValue.replace(/[^0-9]/g, '');
    const normalizedDuplicateTrunkPrefix = /^00(60|70|80|90)\d{7,8}$/.test(stripped)
        ? stripped.replace(/^00/, '0')
        : stripped;
    // add leading 0 if user typed 60XXXXXXXX (10 digits, trunk 0 omitted)
    const normalized060 = /^60\d{8}$/.test(normalizedDuplicateTrunkPrefix)
        ? `0${normalizedDuplicateTrunkPrefix}`
        : normalizedDuplicateTrunkPrefix;
    if (/^0(60|70|80|90)/.test(normalized060)) {
        return normalized060.length === 11
            ? `${normalized060.slice(0, 3)}-${normalized060.slice(3, 7)}-${normalized060.slice(7)}`
            : normalized060 !== stripped
            ? normalized060
            : newValue;
    }

    const candidateValue =
        normalizedDuplicateTrunkPrefix !== stripped ? normalizedDuplicateTrunkPrefix : newValue;
    const phoneNumber = parsePhoneNumberFromString(candidateValue, 'JP');

    if (phoneNumber) {
        const formatNational = phoneNumber.formatNational();
        if (validateJPPhoneNumber(formatNational)) {
            return formatNational;
        }
    }

    return candidateValue;
}

export function phoneNumberOnChange(
    event: React.ChangeEvent<HTMLInputElement>,
    currentCallingCode: string,
    currentCountry?: string,
): string {
    event.target.value = collapseDuplicateJPMobileTrunkPrefix(
        event.target.value,
        currentCountry,
        currentCallingCode,
    );
    event.target.value = enforceFormat(event.target.value);
    // when calling code is undefined allow user to delete whatever
    if (currentCallingCode === 'undefined') {
        if (event.target.value === '') {
            return ' ';
        }
        return event.target.value;
    }

    // return calling code when calling code is left or all is deleted
    if (
        event.target.value.length <= currentCallingCode.length ||
        event.target.value.trim() === ''
    ) {
        return currentCallingCode;
    }
    // trim consecutative whitespaces to one
    const formattedIntlNum = formatInternationalAsString(event.target.value);
    return formattedIntlNum;
}
